import React, { memo, useCallback } from "react";
import { View, StyleSheet } from "react-native";
import Text from "@components/CustomText";
import color from "@constants/color";
import { GIGS_COPY } from "../copy";
import type { GigDay, LocalSelection } from "../types";
import YesNoPill from "./YesNoPill";

/** Caption is driven purely by `response`; `is_locked` only gates the pill's interactivity. */
const getRowCaption = (response: GigDay["response"]): string => {
  if (response === "YES") return GIGS_COPY.status.onDuty;
  if (response === "NO") return GIGS_COPY.status.off;
  return GIGS_COPY.marking.notMarkedYet;
};

interface DayRowProps {
  day: GigDay;
  label: string;
  /** Effective selection for this row — a local edit if present, else `day.response`. */
  selection: LocalSelection;
  onChange: (gigDate: string, response: "YES" | "NO") => void;
}

/**
 * One day's row on the marking screen. `is_locked` is server-derived and only
 * gates whether the pill responds to taps — the app never computes a cutoff
 * from the device clock.
 */
const DayRow: React.FC<DayRowProps> = ({ day, label, selection, onChange }) => {
  const handleChange = useCallback(
    (response: "YES" | "NO") => onChange(day.gig_date, response),
    [day.gig_date, onChange],
  );

  return (
    <View style={styles.row} testID={`gigs-day-row-${day.gig_date}`}>
      <View style={styles.textBlock}>
        <Text variant="title_16" style={styles.dateLabel}>
          {label}
        </Text>
        <Text variant="body_text_3" style={styles.caption}>
          {getRowCaption(day.response)}
        </Text>
      </View>
      <YesNoPill
        value={selection}
        disabled={day.is_locked}
        onChange={handleChange}
        testID={`gigs-day-pill-${day.gig_date}`}
      />
    </View>
  );
};

export default memo(DayRow);

const styles = StyleSheet.create({
  row: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: color.lineColor,
  },
  textBlock: {
    flex: 1,
    paddingRight: 12,
  },
  dateLabel: {
    color: color.mediumBlack,
  },
  caption: {
    color: color.lightMediumBlack,
    marginTop: 2,
  },
});
